/**
 * routeLoaders.ts — one dynamic import per page, shared by React.lazy (App.tsx)
 * and by prefetching (Layout.tsx). Prefetching downloads a page's code before
 * you click it, so moving between pages shows no empty flash.
 */
export const loaders = {
  review: () => import("./pages/Review").then((m) => ({ default: m.Review })),
  // Ads
  adsThisWeek: () => import("./pages/ads/ThisWeek").then((m) => ({ default: m.AdsThisWeek })),
  adsConcepts: () => import("./pages/ads/Concepts").then((m) => ({ default: m.AdsConcepts })),
  adsList: () => import("./pages/ads/AdsList").then((m) => ({ default: m.AdsList })),
  adsResearch: () => import("./pages/ads/Research").then((m) => ({ default: m.AdsResearch })),
  adsGoogle: () => import("./pages/ads/Google").then((m) => ({ default: m.AdsGoogle })),
  adsResults: () => import("./pages/ads/Results").then((m) => ({ default: m.AdsResults })),
  adsContext: () => import("./pages/ads/Context").then((m) => ({ default: m.AdsContext })),
  // Social
  socialThisWeek: () => import("./pages/social/ThisWeek").then((m) => ({ default: m.SocialThisWeek })),
  socialIdeas: () => import("./pages/social/Ideas").then((m) => ({ default: m.SocialIdeas })),
  socialPosts: () => import("./pages/social/Posts").then((m) => ({ default: m.SocialPostsPage })),
  socialChannels: () => import("./pages/social/Channels").then((m) => ({ default: m.SocialChannelsPage })),
  socialResults: () => import("./pages/social/Results").then((m) => ({ default: m.SocialResultsPage })),
  socialContext: () => import("./pages/social/Context").then((m) => ({ default: m.SocialContext })),
  // Content
  contentList: () => import("./pages/ContentList").then((m) => ({ default: m.ContentList })),
  contentEditor: () => import("./pages/ContentEditor").then((m) => ({ default: m.ContentEditor })),
  calendar: () => import("./pages/Calendar").then((m) => ({ default: m.Calendar })),
  taxonomy: () => import("./pages/content/Taxonomy").then((m) => ({ default: m.ContentTaxonomy })),
  siteGit: () => import("./pages/content/SiteGit").then((m) => ({ default: m.ContentSiteGit })),
  aiIdeas: () => import("./pages/AiIdeas").then((m) => ({ default: m.AiIdeas })),
  aiDraftList: () => import("./pages/AiDrafts").then((m) => ({ default: m.AiDraftList })),
  aiDraftReview: () => import("./pages/AiDrafts").then((m) => ({ default: m.AiDraftReview })),
  brandMemory: () => import("./pages/BrandMemory").then((m) => ({ default: m.BrandMemory })),
  evidence: () => import("./pages/Evidence").then((m) => ({ default: m.Evidence })),
  // Operations
  opsRuns: () => import("./pages/ops/Runs").then((m) => ({ default: m.OpsRuns })),
  opsCosts: () => import("./pages/ops/Costs").then((m) => ({ default: m.OpsCosts })),
  opsImports: () => import("./pages/ops/Imports").then((m) => ({ default: m.OpsImports })),
  opsIntegrations: () => import("./pages/ops/Integrations").then((m) => ({ default: m.OpsIntegrations })),
  auditLog: () => import("./pages/AuditLog").then((m) => ({ default: m.AuditLog })),
  // Settings
  settingsProducts: () => import("./pages/settings/Products").then((m) => ({ default: m.SettingsProducts })),
  settingsPeople: () => import("./pages/settings/People").then((m) => ({ default: m.SettingsPeople })),
  settingsApiKeys: () => import("./pages/settings/ApiKeysPage").then((m) => ({ default: m.SettingsApiKeys })),
  settingsPrompts: () => import("./pages/settings/PromptsPage").then((m) => ({ default: m.SettingsPrompts })),
  security: () => import("./pages/Security").then((m) => ({ default: m.Security })),
} as const;

/** Which loaders belong to a URL prefix (used when the pointer hovers a nav link). */
const BY_PATH: Array<[string, Array<keyof typeof loaders>]> = [
  ["/review", ["review"]],
  ["/ads/this-week", ["adsThisWeek"]],
  ["/ads/concepts", ["adsConcepts"]],
  ["/ads/ads", ["adsList"]],
  ["/ads/research", ["adsResearch"]],
  ["/ads/google", ["adsGoogle"]],
  ["/ads/results", ["adsResults"]],
  ["/ads/context", ["adsContext"]],
  ["/social/this-week", ["socialThisWeek"]],
  ["/social/ideas", ["socialIdeas"]],
  ["/social/posts", ["socialPosts"]],
  ["/social/channels", ["socialChannels"]],
  ["/social/results", ["socialResults"]],
  ["/social/context", ["socialContext"]],
  ["/content/taxonomy", ["taxonomy"]],
  ["/content", ["contentList", "contentEditor"]],
  ["/calendar", ["calendar"]],
  ["/git", ["siteGit"]],
  ["/ai/ideas", ["aiIdeas"]],
  ["/ai/evidence", ["evidence"]],
  ["/ai/drafts", ["aiDraftList", "aiDraftReview"]],
  ["/ai/memory", ["brandMemory"]],
  ["/ops/runs", ["opsRuns"]],
  ["/ops/costs", ["opsCosts"]],
  ["/ops/imports", ["opsImports"]],
  ["/ops/integrations", ["opsIntegrations"]],
  ["/audit", ["auditLog"]],
  ["/settings/products", ["settingsProducts"]],
  ["/users", ["settingsPeople"]],
  ["/settings/api-keys", ["settingsApiKeys"]],
  ["/settings/prompts", ["settingsPrompts"]],
];

export function prefetchPath(path: string): void {
  const hit = BY_PATH.find(([prefix]) => path.startsWith(prefix));
  hit?.[1].forEach((k) => void loaders[k]().catch(() => undefined));
}

/** After the first screen has painted, fetch every other page's code in the background. */
export function prefetchAllWhenIdle(): void {
  const run = () => (Object.keys(loaders) as Array<keyof typeof loaders>).forEach((k) => void loaders[k]().catch(() => undefined));
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (ric) ric(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}
